import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import { Button } from "../../../../components/ui/button";
import { ArrowUpDown, Trash2 } from "lucide-react";
import {
  FieldGroup,
  FieldGroupInstance,
  getInstanceDisplaySlug,
} from "../../../../models/audit/library";
import { cn } from "../../../../lib/utils";

interface LibraryTableProps {
  instances: FieldGroupInstance[];
  fieldGroup: FieldGroup | null;
  onInstanceClick: (instance: FieldGroupInstance) => void;
  onDeleteInstance: (instance: FieldGroupInstance) => void;
  lastInstanceRef: (node: HTMLTableRowElement | null) => void;
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (field: string) => void;
  loading: boolean;
  loadingDelete: boolean;
  canEdit: boolean;
}

export function LibraryTable({
  instances,
  fieldGroup,
  onInstanceClick,
  onDeleteInstance,
  lastInstanceRef,
  sortBy,
  sortOrder: _sortOrder,
  onSort,
  loading,
  loadingDelete,
  canEdit,
}: LibraryTableProps) {
  // Note: sortOrder is passed to parent but not currently used for visual indicator
  void _sortOrder;

  const renderSortHeader = (
    field: string,
    label: string,
    sortField?: string,
  ) => (
    <Button
      variant="ghost"
      className="h-auto p-0 hover:bg-transparent font-medium"
      onClick={() => onSort(sortField ?? field)}
    >
      {label}
      <ArrowUpDown
        className={cn(
          "ml-1 h-3 w-3",
          sortBy === (sortField ?? field) ? "text-stone-900" : "text-stone-400",
        )}
      />
    </Button>
  );

  if (instances.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-stone-500">
        <p>No items found</p>
        <p className="text-sm">Add items to this library to get started</p>
      </div>
    );
  }

  return (
    <Table className="w-full">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[150px]">
            {renderSortHeader(
              "slug",
              fieldGroup?.slugPrefix ? `${fieldGroup.slugPrefix} ID` : "ID",
              "slug", // Maps to slug_suffix in the hook
            )}
          </TableHead>
          <TableHead className="w-full">
            {renderSortHeader("name", "Name")}
          </TableHead>
          {canEdit && <TableHead className="w-[50px]" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {instances.map((instance, index) => {
          const isLast = index === instances.length - 1;
          return (
            <TableRow
              key={instance.id}
              ref={isLast ? lastInstanceRef : undefined}
              className="cursor-pointer hover:bg-stone-50"
              onClick={() => onInstanceClick(instance)}
            >
              <TableCell className="font-medium">
                {getInstanceDisplaySlug(instance, fieldGroup)}
              </TableCell>
              <TableCell>{instance.name}</TableCell>
              {canEdit && (
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-stone-400 hover:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteInstance(instance);
                    }}
                    disabled={loadingDelete}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          );
        })}
        {loading && (
          <TableRow>
            <TableCell colSpan={canEdit ? 3 : 2} className="text-center py-4">
              Loading more...
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
